/* Public booking pages (Phase B2) — client-facing design tier.
 *
 * Scoped entirely under .book-public so nothing leaks into the internal
 * tool. To a first-time prospect this page IS the brand: warm, premium,
 * unhurried — a boutique firm's front room, not an admin tool. Palette
 * is the Stewardship design system (navy/blue) declared once as custom
 * properties on the root class.
 */

.book-public {
  --bk-navy: #022c51;
  --bk-blue: #4e98c6;
  --bk-blue-light: #5ba3d0;
  --bk-blue-dark: #3a7ea8;
  /* Accent rule only — a 3-4px top border on a card. Never a fill, never
     a button, never text (white on yellow is 1.4:1). */
  --bk-yellow: #fed766;
  --bk-bg: #f4f6f8;
  --bk-border: #e0e6ec;
  --bk-text: #333333;
  --bk-white: #ffffff;
  /* Navy-tinted elevation scale, matching web/src/design/tokens.ts's
     `shadows.sm`/`shadows.md` (2026-08-25 audit D1) — book.css is plain
     CSS with no import path to that file, so the values are mirrored
     here rather than imported. */
  --bk-shadow-sm: 0 2px 8px rgba(2, 44, 81, 0.08);
  --bk-shadow-md: 0 4px 16px rgba(2, 44, 81, 0.12);

  min-height: 100vh;
  background: var(--bk-bg);
  color: var(--bk-text);
  font-family: "Open Sans", system-ui, -apple-system, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}

.book-public *,
.book-public *::before,
.book-public *::after {
  box-sizing: border-box;
}

/* ── Shell ── */

.book-shell {
  max-width: 720px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ── The navy hero band (2026-09-04 redesign) ──
   Every screen's headline sits on the brand's own hero treatment:
   navy gradient + grain + glow orbs (never a video), with the step's
   first card lifting out of the band's bottom edge. The values mirror
   the Stewardship design system's hero spec; the grain is an inline
   SVG fractalNoise at 4% so it costs no request. */

.book-hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #011e38 0%, var(--bk-navy) 55%, #033d70 100%);
  color: var(--bk-white);
  padding-bottom: 104px;
}

.book-hero::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.04;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
}

.book-orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}

.book-orb--1 {
  width: 760px;
  height: 760px;
  top: -300px;
  right: -180px;
  background: radial-gradient(circle, rgba(78, 152, 198, 0.22) 0%, rgba(78, 152, 198, 0) 68%);
}

.book-orb--2 {
  width: 620px;
  height: 620px;
  bottom: -320px;
  left: -220px;
  background: radial-gradient(circle, rgba(78, 152, 198, 0.15) 0%, rgba(78, 152, 198, 0) 68%);
}

.book-orb--3 {
  width: 520px;
  height: 520px;
  bottom: -280px;
  right: 10%;
  background: radial-gradient(circle, rgba(254, 215, 102, 0.06) 0%, rgba(254, 215, 102, 0) 68%);
}

.book-hero .book-shell {
  position: relative;
}

.book-hero-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  padding-top: 40px;
}

.book-hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "Gotham", "Montserrat", "Open Sans", sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 12.5px;
  color: var(--bk-blue-light);
  margin-top: 8px;
}

.book-hero-title {
  font-family: "Gotham", "Montserrat", "Open Sans", sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  /* trackingForSize's ≥30px bracket — caps this large need almost none. */
  letter-spacing: 0.01em;
  font-size: clamp(32px, 4.2vw, 52px);
  line-height: 1.08;
  color: var(--bk-white);
  margin: 0;
  max-width: 760px;
  text-wrap: balance;
}

/* The yellow swoosh under the title's last word — the ONE place yellow
   appears at this size (brand rule: swoosh beneath H1/H2 only). */
.book-swoosh {
  position: relative;
  display: inline-block;
  white-space: nowrap;
}

.book-swoosh svg {
  position: absolute;
  left: -2%;
  bottom: -0.16em;
  width: 104%;
  height: 0.28em;
  overflow: visible;
}

.book-hero-kicker {
  font-size: 20px;
  font-weight: 700;
  color: var(--bk-white);
}

.book-hero-lede {
  margin: 0;
  max-width: 560px;
  font-size: 17px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.82);
  text-wrap: pretty;
}

.book-hero-back .book-link-btn,
.book-hero-back a {
  color: rgba(255, 255, 255, 0.78);
  font-size: 14.5px;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 8px;
}

.book-hero-back .book-link-btn:hover,
.book-hero-back a:hover {
  color: var(--bk-white);
}

/* Progress: Topic · Who · When · Details. The current step is the one
   yellow badge (badge fill with navy text — the brand's allowed use). */
.book-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  list-style: none;
  margin: 0;
  padding: 0;
}

.book-step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Gotham", "Montserrat", sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.55);
}

.book-step-item + .book-step-item::before {
  content: "";
  display: block;
  width: 34px;
  height: 1px;
  background: rgba(255, 255, 255, 0.22);
  margin: 0 12px;
}

.book-step-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  font-family: "Open Sans", sans-serif;
  font-weight: 800;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  flex: none;
}

.book-step-item--done {
  color: rgba(255, 255, 255, 0.8);
}

.book-step-item--done .book-step-dot {
  background: var(--bk-blue);
  border-color: var(--bk-blue);
  color: var(--bk-white);
}

.book-step-item--now {
  color: var(--bk-white);
}

.book-step-item--now .book-step-dot {
  background: var(--bk-yellow);
  border-color: var(--bk-yellow);
  color: var(--bk-navy);
}

/* The work area lifts its first surface out of the band. */
.book-body {
  position: relative;
  margin-top: -64px;
  padding-bottom: 8px;
}

/* The front door's booking layout earns real width on desktop (the
   2026-08-27 redesign — the old single 720px column left half the
   screen as empty margin and squeezed every card until text wrapped
   badly). The invite/manage pages stay on the comfortable 720. */
.book-shell--wide {
  max-width: 1080px;
}

/* Steps that are genuinely one narrow conversation (a clarifying
   question, a dead-end notice) center themselves inside the wide
   shell instead of stretching across it. */
.book-narrow {
  max-width: 640px;
  margin: 0 auto;
}

.book-mid {
  max-width: 900px;
  margin: 0 auto;
}

/* A light header band, not a bare centered wordmark (audit D2,
   2026-08-25) — the phone number belongs where every screen's first
   glance lands, not only in the footer. */
.book-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 24px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.book-wordmark {
  font-family: "Gotham", "Montserrat", "Open Sans", sans-serif;
  font-weight: 800;
  letter-spacing: 0.18em;
  font-size: 18px;
  color: var(--bk-white);
  text-transform: uppercase;
}

.book-header-call {
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  white-space: nowrap;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
}

.book-header-call:hover {
  color: var(--bk-white);
}

/* A footer BAND — bordered and roomy, not a fading line of fine print
   (audit D2) — and it reinforces C4 (the office address, once set). */
.book-footer {
  margin: 40px 0 0;
  padding: 28px 20px 36px;
  border-top: 1px solid var(--bk-border);
  text-align: center;
  font-size: 14px;
  color: var(--bk-navy);
  opacity: 0.75;
}

.book-footer a {
  color: inherit;
  font-weight: 700;
}

/* The office address (audit C4) — a quiet second line under the phone
   fallback, not competing with it for attention. */
.book-footer-address {
  margin-top: 6px;
  font-size: 13px;
}

/* ── Headings & copy ── */

/* The page's only H1 lives in the hero band (.book-hero-title). */

.book-public h2 {
  font-family: "Gotham", "Montserrat", "Open Sans", sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 17px;
  color: var(--bk-navy);
  margin: 28px 0 12px;
  text-wrap: balance;
}

.book-lede {
  text-align: center;
  font-size: 17px;
  margin: 0 auto 24px;
  max-width: 560px;
  color: var(--bk-text);
  text-wrap: pretty;
}

.book-lede-phone {
  display: block;
  margin-top: 2px;
}

/* A quiet eyebrow, not another shouting H2 (audit D4, 2026-08-25) —
   "WHAT CAN WE HELP YOU WITH?" reads as a section label, not a
   heading competing with the page's own H1. Tracking follows
   trackingForSize's 12.5px bracket (0.10em) from
   web/src/design/tokens.ts. */
.book-eyebrow {
  font-family: "Gotham", "Montserrat", "Open Sans", sans-serif;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 12.5px;
  color: var(--bk-navy);
  opacity: 0.55;
  margin: 28px 0 14px;
}

.book-needs-grid .book-choice-label {
  font-size: 16px;
  line-height: 1.35;
  text-wrap: balance;
}

.book-quiet {
  font-size: 14px;
  color: var(--bk-navy);
  opacity: 0.7;
}

.book-tz {
  font-size: 13px;
  color: var(--bk-navy);
  opacity: 0.75;
  font-weight: 600;
}

/* ── Cards & buttons ── */

.book-card {
  background: var(--bk-white);
  border: 1px solid var(--bk-border);
  border-radius: 10px;
  padding: 20px;
  box-shadow: var(--bk-shadow-sm);
}

.book-card + .book-card {
  margin-top: 14px;
}

/* Brand primary button: three-stop blue gradient, Gotham caps, 6px
   radius, navy-tinted shadow, hover lift (2026-09-04 redesign — the old
   button was a two-stop gradient in Open Sans at a 10px radius, the
   one control on the page that didn't match the design system). */
.book-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: "Gotham", "Montserrat", "Open Sans", sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 13.5px;
  color: var(--bk-white);
  background: linear-gradient(180deg, var(--bk-blue-light) 0%, var(--bk-blue) 50%, var(--bk-blue-dark) 100%);
  border: none;
  border-radius: 6px;
  min-height: 48px;
  padding: 0 24px;
  cursor: pointer;
  width: 100%;
  box-shadow: 0 3px 10px rgba(2, 44, 81, 0.25);
  transition: transform 150ms ease, box-shadow 150ms ease;
}

.book-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(2, 44, 81, 0.3);
}

.book-btn:active:not(:disabled) {
  transform: translateY(0);
}

/* Navy-fill secondary (the brand's secondary button). */
.book-btn--navy {
  background: var(--bk-navy);
  box-shadow: 0 3px 10px rgba(2, 44, 81, 0.18);
}

/* Darker, deliberately-inert (audit D8, 2026-08-25) — the old 0.55
   opacity on the same blue gradient read as "still loading," not "not
   ready yet." */
.book-btn:disabled {
  opacity: 1;
  background: var(--bk-border);
  color: var(--bk-navy);
  filter: none;
  cursor: default;
  box-shadow: none;
}

/* A deliberate, branded ring — not the thin default-looking outline
   (audit D11): navy, thicker, floating off the edge rather than
   hugging it. Stays on `outline` (not box-shadow) so it layers
   independently over a selected/elevated state's own box-shadow
   instead of replacing it. */
.book-btn:focus-visible,
.book-choice:focus-visible,
.book-slot:focus-visible,
.book-pill:focus-visible,
.book-daychip:focus-visible,
.book-link-btn:focus-visible {
  outline: 3px solid var(--bk-navy);
  outline-offset: 3px;
}

.book-btn--secondary {
  background: var(--bk-white);
  color: var(--bk-navy);
  border: 1.5px solid var(--bk-border);
}

.book-link-btn {
  background: none;
  border: none;
  color: var(--bk-blue-dark);
  font: inherit;
  font-size: 15px;
  text-decoration: underline;
  cursor: pointer;
  padding: 4px;
}

/* ── Choice grids (needs, advisors, types) & pills ── */

.book-choice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 12px;
  margin: 16px 0;
}

.book-choice {
  text-align: left;
  background: var(--bk-white);
  border: 1.5px solid var(--bk-border);
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  /* Real elevation at rest, not box-shadow:none on a grey page (audit
     D1) — with a slight lift on hover, not just a bigger shadow. */
  box-shadow: var(--bk-shadow-sm);
  transition: border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
}

.book-choice:hover {
  border-color: var(--bk-blue);
  box-shadow: var(--bk-shadow-md);
  transform: translateY(-2px);
}

.book-choice[aria-pressed="true"],
.book-choice--selected {
  border-color: var(--bk-blue-dark);
  box-shadow: inset 0 0 0 1.5px var(--bk-blue-dark);
}

.book-choice-label {
  display: block;
  font-weight: 700;
  color: var(--bk-navy);
  margin-bottom: 4px;
}

.book-choice-desc {
  display: block;
  font-size: 14px;
  opacity: 0.85;
}

/* The need grid (2026-08-27 redesign): three columns of compact
   icon-left rows on desktop, so all nine topics sit above the fold —
   the old two tall columns of icon-above-title cards took three
   screens of scrolling. Descriptions show in FULL: the old 2-line
   clamp chopped real sentences mid-thought ("…or a look at your whole
   financial…"), and per the Accuracy doctrine a card that silently
   truncates its own words isn't telling the truth. */
.book-needs-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 16px 0;
}

@media (max-width: 900px) {
  .book-needs-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .book-needs-grid {
    grid-template-columns: 1fr;
  }
}

/* Icon left, words right — one compact row per card. */
.book-need {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
}

.book-need-text {
  display: block;
  min-width: 0;
}

.book-need-icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: rgba(78, 152, 198, 0.12);
  color: var(--bk-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.book-needs-grid .book-choice-desc {
  font-size: 13.5px;
  line-height: 1.45;
  text-wrap: pretty;
}

/* Small quiet meta line on a choice card (e.g. "30 minutes"). */
.book-choice-meta {
  display: block;
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--bk-navy);
  opacity: 0.6;
}

/* The topic the client picked, carried through the flow as a quiet
   confirmation chip — the thread that says "you're in the right
   place." */
.book-topic-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(78, 152, 198, 0.12);
  color: var(--bk-navy);
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 14px;
  font-weight: 700;
  margin: 0 auto 14px;
}

.book-mid .book-topic-chip {
  display: flex;
  width: fit-content;
}

.book-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 10px 0 4px;
}

.book-pill {
  border: 1.5px solid var(--bk-border);
  background: var(--bk-white);
  border-radius: 999px;
  padding: 8px 18px;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  color: var(--bk-navy);
  cursor: pointer;
}

.book-pill[aria-pressed="true"] {
  background: var(--bk-navy);
  border-color: var(--bk-navy);
  color: var(--bk-white);
}

/* "Already know who you want to book with?" (audit D5, 2026-08-25) — a
   real secondary affordance, not a bare underlined link buried under
   the need cards. */
.book-people {
  margin: 28px 0 0;
  text-align: center;
}

/* The team list (2026-08-27 redesign): avatar-left rows wide enough
   that a name never breaks in half — the old 215px cards wrapped
   "Grant Botma" onto two lines and long credentials onto four. */
.book-people-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  margin: 16px 0;
  text-align: left;
}

.book-person {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
}

.book-person .book-avatar {
  width: 52px;
  height: 52px;
  font-size: 18px;
}

.book-person-text {
  display: block;
  min-width: 0;
}

.book-person .book-choice-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.book-person .book-choice-desc {
  font-size: 13px;
  line-height: 1.35;
  /* A long credential gets two tidy lines, never four ragged ones. */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.book-affordance-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--bk-white);
  border: 1.5px solid var(--bk-border);
  border-radius: 999px;
  padding: 10px 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  color: var(--bk-navy);
  cursor: pointer;
  box-shadow: var(--bk-shadow-sm);
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

.book-affordance-pill:hover {
  border-color: var(--bk-blue);
  box-shadow: var(--bk-shadow-md);
}

/* ── Choose who you'd like to meet (2026-09-04 redesign) ──
   Two (or three) large person cards, each with a ringed photo, the
   name in Gotham, the credential on one line, a one-sentence bio, the
   real next opening from their calendar, and a "Book with Brian"
   button. The old step reused the 210px topic-tile grid, which wrapped
   "CERTIFIED FINANCIAL PLANNER™ Professional" to four lines and ran
   "Jake Norton" off its card. */

.book-advisor-choices {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
}

.book-advisor-choices--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

/* The grid's gap is the spacing; the stacked-cards rule
   (.book-card + .book-card) must not nudge the second card down. */
.book-advisor-choices > .book-card + .book-card {
  margin-top: 0;
}

.book-advisor-choice {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 28px;
  border-radius: 12px;
  box-shadow: var(--bk-shadow-md);
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}

.book-advisor-choice:hover {
  border-color: var(--bk-blue);
  box-shadow: 0 0 0 3px rgba(78, 152, 198, 0.18), var(--bk-shadow-md);
  transform: translateY(-3px);
}

.book-advisor-choice-head {
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;
}

.book-advisor-choice .book-avatar {
  width: 88px;
  height: 88px;
  font-size: 30px;
  /* A white ring with a blue-tint halo — the photo reads as the card's
     focal point instead of a thumbnail. */
  box-shadow: 0 0 0 4px var(--bk-white), 0 0 0 6px var(--bk-blue-tint, #deeef7);
}

.book-advisor-choice-who {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.book-advisor-choice-name {
  font-family: "Gotham", "Montserrat", "Open Sans", sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 22px;
  line-height: 1.15;
  color: var(--bk-navy);
}

.book-advisor-choice-title {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--bk-blue-dark);
  text-wrap: pretty;
}

.book-advisor-choice-bio {
  margin: 0;
  /* Grows so the opening row + button line up across cards whose bios
     differ in length. */
  flex: 1 1 auto;
  font-size: 15px;
  line-height: 1.65;
  text-wrap: pretty;
}

.book-advisor-choice-rule {
  height: 1px;
  background: var(--bk-border);
}

.book-advisor-choice-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.book-opening {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.book-opening-icon {
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: #deeef7;
  color: var(--bk-blue-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.book-opening-label {
  font-family: "Gotham", "Montserrat", sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 11px;
  color: var(--bk-navy);
  opacity: 0.55;
}

.book-opening-value {
  font-size: 15px;
  font-weight: 700;
  color: var(--bk-navy);
  line-height: 1.3;
  white-space: nowrap;
}

.book-opening-value--none {
  font-weight: 600;
  opacity: 0.7;
  white-space: normal;
}

.book-advisor-choice .book-btn {
  width: auto;
  flex: none;
}

/* "No preference — first available": a secondary full-width row UNDER
   the people, not a look-alike card beside them. */
.book-first-available {
  margin-top: 20px;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  gap: 18px;
}

.book-first-available-text {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 1 auto;
  min-width: 0;
}

.book-first-available-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #deeef7;
  color: var(--bk-blue-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.book-first-available-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--bk-navy);
  line-height: 1.35;
  text-wrap: pretty;
}

.book-first-available-sub {
  font-size: 14px;
  color: #4a5b6e;
  line-height: 1.45;
  text-wrap: pretty;
}

.book-first-available .book-btn {
  width: auto;
  flex: none;
}

@media (max-width: 880px) {
  .book-advisor-choices,
  .book-advisor-choices--three {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .book-advisor-choice {
    padding: 22px;
    gap: 16px;
  }
  .book-advisor-choice .book-avatar {
    width: 72px;
    height: 72px;
    font-size: 24px;
  }
  .book-advisor-choice-name {
    font-size: 19px;
  }
  .book-advisor-choice-foot {
    flex-direction: column;
    align-items: stretch;
  }
  .book-advisor-choice .book-btn,
  .book-first-available .book-btn {
    width: 100%;
  }
  .book-first-available {
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
    gap: 16px;
  }
}

/* ── Advisor ── */

.book-advisor {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

/* A compact advisor row (a choice-grid card, the people list) gives its
   text room instead of breaking a name awkwardly mid-word (audit D12,
   2026-08-25) — it truncates instead. The larger advisor panel
   (.book-advisor-panel) has room to wrap normally and isn't affected. */
.book-choice .book-advisor-name,
.book-people .book-advisor-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.book-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  flex: none;
  background: var(--bk-navy);
  color: var(--bk-white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Gotham", "Montserrat", sans-serif;
  font-weight: 800;
  letter-spacing: 0.02em;
  font-size: 24px;
}

.book-advisor-name {
  font-family: "Gotham", "Montserrat", sans-serif;
  font-weight: 800;
  letter-spacing: 0.02em;
  font-size: 18px;
  color: var(--bk-navy);
}

.book-advisor-bio {
  margin-top: 10px;
  font-size: 15px;
}

/* The picked slot on the details summary card (audit C3). */
.book-summary-when {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--bk-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-weight: 700;
  color: var(--bk-navy);
}

/* ── The two-panel booking layout (2026-08-27 redesign) ──
   Once a person is chosen: a sticky summary panel on the left (their
   face, title, bio, the topic the client picked, every answer so far),
   and exactly one question at a time on the right. On phones the
   panel becomes a compact card above the questions. */

.book-layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 28px;
  align-items: start;
  margin-top: 10px;
}

.book-side {
  position: sticky;
  top: 20px;
}

.book-side-card {
  background: var(--bk-white);
  border: 1px solid var(--bk-border);
  border-top: 4px solid var(--bk-blue);
  border-radius: 12px;
  padding: 20px;
  box-shadow: var(--bk-shadow-sm);
}

.book-side-person {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.book-side-card .book-avatar {
  width: 76px;
  height: 76px;
  font-size: 26px;
}

.book-side-who {
  min-width: 0;
}

.book-side-name {
  font-family: "Gotham", "Montserrat", "Open Sans", sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 18px;
  line-height: 1.25;
  color: var(--bk-navy);
  text-wrap: balance;
}

.book-side-title {
  font-size: 13.5px;
  color: var(--bk-navy);
  opacity: 0.7;
  margin-top: 2px;
  text-wrap: pretty;
}

.book-side-bio {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.55;
  text-wrap: pretty;
}

.book-side-topic {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(78, 152, 198, 0.12);
  color: var(--bk-navy);
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 14px;
  font-weight: 700;
  margin-top: 14px;
  text-wrap: pretty;
}

.book-side-topic svg {
  flex: none;
  color: var(--bk-blue-dark);
}

/* The running summary: label left, answer + Change right. */
.book-side-rows {
  margin: 14px 0 0;
}

.book-side-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  border-top: 1px solid var(--bk-border);
  padding: 10px 0;
  font-size: 14px;
}

.book-side-row:last-child {
  padding-bottom: 0;
}

.book-side-row dt {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 11.5px;
  color: var(--bk-navy);
  opacity: 0.55;
  flex: none;
  padding-top: 2px;
}

.book-side-row dd {
  margin: 0;
  text-align: right;
  font-weight: 600;
  color: var(--bk-navy);
  min-width: 0;
}

.book-side-row dd .book-link-btn {
  font-size: 13px;
  margin-left: 8px;
  padding: 0;
}

@media (max-width: 880px) {
  .book-layout {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .book-side {
    position: static;
  }
  .book-side-card {
    padding: 16px;
  }
  .book-side-card .book-avatar {
    width: 56px;
    height: 56px;
    font-size: 20px;
  }
}

/* Step numbers on the advisor panel's questions (audit D6). */
.book-step-heading {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* The questions column is a card of its own (2026-09-04 redesign) —
   it lifts out of the navy band beside the summary card, so its H2
   never lands as navy text on a navy background. */
.book-main {
  background: var(--bk-white);
  border: 1px solid var(--bk-border);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--bk-shadow-sm);
  min-width: 0;
}

/* The main column's first question sits level with the top of the
   summary panel, not 28px below it. */
.book-main > .book-step-heading:first-child h2,
.book-main > h2:first-child,
.book-main > .book-notice:first-child,
.book-main > .book-error:first-child {
  margin-top: 0;
}

/* Choice cards in the main column get room to breathe — the shared
   210px minimum (sized for the old narrow shell) squeezed a meeting
   type's description into a skinny seven-line column. */
.book-main .book-choice-grid {
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}

.book-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--bk-navy);
  color: var(--bk-white);
  font-size: 11px;
  font-weight: 800;
  flex: none;
}

/* Why the gray "Book it" button isn't ready yet — a disabled button
   with no reason reads as broken. */
.book-missing-hint {
  font-size: 13.5px;
  color: var(--bk-navy);
  opacity: 0.7;
  margin: 8px 0 0;
  text-wrap: pretty;
}

/* ── Slot picker ── */
/* Header row restructured (audit D7, 2026-08-25): week-range label
   left, Earlier/Later arrows right, "Arizona time" on its own line
   beneath — the caption and the Later→ link used to share one
   misaligned row. */

.book-week-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
}

.book-week-range {
  font-family: "Gotham", "Montserrat", "Open Sans", sans-serif;
  font-weight: 800;
  letter-spacing: 0.03em;
  font-size: 15px;
  color: var(--bk-navy);
}

.book-week-arrows {
  display: flex;
  align-items: center;
  gap: 14px;
}

.book-week-nav {
  margin: 2px 0 10px;
}

.book-time-period {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--bk-navy);
  opacity: 0.6;
  margin: 14px 0 8px;
}

.book-time-period:first-child {
  margin-top: 0;
}

/* The step's own back link, above the content rather than trailing it. */
.book-back--top {
  margin: -4px 0 16px;
}

/* Day-first picker (2026-08-17). The old layout was a grid of day
   COLUMNS with every opening stacked inside, so a busy day made a
   thirty-button column and the days that wrapped landed a screen and a
   half below. Now: one row of days, then that day's times. */
.book-daystrip {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
  margin-bottom: 18px;
}

.book-daychip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 2px;
  border: 1.5px solid var(--bk-border);
  border-radius: 10px;
  background: var(--bk-white);
  color: var(--bk-navy);
  font: inherit;
  cursor: pointer;
  transition: border-color 100ms ease, background 100ms ease, color 100ms ease;
}

.book-daychip:hover:not(:disabled) {
  border-color: var(--bk-blue);
}

.book-daychip[aria-pressed="true"] {
  background: var(--bk-navy);
  border-color: var(--bk-navy);
  color: var(--bk-white);
}

/* A closed day reads as a closed day, not a rendering bug (audit D7,
   2026-08-25) — greyed background AND text, "Closed" instead of a bare
   dash that looked like missing content. */
.book-daychip:disabled {
  cursor: default;
  opacity: 0.55;
  background: var(--bk-bg);
  color: var(--bk-text);
}

.book-daychip-weekday {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.book-daychip-num {
  font-size: 19px;
  font-weight: 800;
  line-height: 1.1;
}

.book-daychip-meta {
  font-size: 10.5px;
  opacity: 0.75;
  white-space: nowrap;
}

.book-daychip-skeleton {
  height: 62px;
  margin-bottom: 0;
}

/* The chosen day's times — wrapping chips, never one tall column. */
.book-times {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  gap: 8px;
}

.book-day-label {
  font-weight: 700;
  font-size: 13px;
  color: var(--bk-navy);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.book-slot {
  display: block;
  width: 100%;
  border: 1.5px solid var(--bk-blue);
  background: var(--bk-white);
  color: var(--bk-blue-dark);
  border-radius: 8px;
  padding: 9px 6px;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background 100ms ease, color 100ms ease;
}

.book-slot:hover,
.book-slot[aria-pressed="true"] {
  background: var(--bk-navy);
  border-color: var(--bk-navy);
  color: var(--bk-white);
}

/* Loading skeletons — never a spinner-only screen. */
.book-skeleton {
  border-radius: 8px;
  background: linear-gradient(90deg, var(--bk-border) 25%, #edf1f5 50%, var(--bk-border) 75%);
  background-size: 200% 100%;
  animation: book-shimmer 1.2s infinite linear;
  height: 38px;
  margin-bottom: 8px;
}

@keyframes book-shimmer {
  to {
    background-position: -200% 0;
  }
}

/* ── Forms ── */

.book-field {
  margin-bottom: 14px;
}

.book-field label {
  display: block;
  font-weight: 700;
  font-size: 14px;
  color: var(--bk-navy);
  margin-bottom: 6px;
}

.book-field input,
.book-field select,
.book-field textarea {
  width: 100%;
  border: 1.5px solid var(--bk-border);
  border-radius: 8px;
  padding: 12px 14px;
  font: inherit;
  font-size: 16px;
  color: var(--bk-text);
  background: var(--bk-white);
}

.book-field input:focus,
.book-field select:focus,
.book-field textarea:focus {
  outline: none;
  border-color: var(--bk-blue);
  box-shadow: 0 0 0 3px rgba(78, 152, 198, 0.2);
}

.book-hint {
  font-size: 13px;
  opacity: 0.7;
  margin-top: 4px;
}

/* Honeypot: visually hidden, still in the form for bots to find. */
.book-hp {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.book-error {
  background: #fdf3f2;
  border: 1.5px solid #e6b8b3;
  border-radius: 10px;
  padding: 14px 16px;
  color: #7a2e25;
  font-size: 15px;
  margin: 14px 0;
}

.book-notice {
  background: #f2f7fb;
  border: 1.5px solid var(--bk-border);
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 15px;
  margin: 14px 0;
}

/* "Sarah already booked this" — the household link's second reader
   (2026-09-09). Yellow top rule so it reads as a heads-up rather than
   an error, and the way past it sits inside the notice. */
.book-already {
  border-top: 4px solid var(--bk-yellow, #fed766);
}
.book-already .book-link-btn {
  display: block;
  margin-top: 10px;
}

/* ── Ticket-style summary card (audit D8/D9, 2026-08-25) ── */

.book-ticket {
  border-top: 4px solid var(--bk-blue);
  padding-top: 16px;
}

.book-ticket-when {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed var(--bk-border);
  font-weight: 800;
  font-size: 17px;
  color: var(--bk-navy);
}

/* ── The prep card: the questions, presented as the next step ──
   Grant, 2026-09-04. Yellow top border rather than the ticket's blue so
   the two cards read as different things at a glance — one is what you
   just did, one is what to do next. Yellow stays a 3px accent rule, never
   a fill, per the brand rule. */

.book-prep {
  border-top: 4px solid var(--bk-yellow);
  margin-top: 18px;
  text-align: center;
}

.book-prep-eyebrow {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--bk-blue-dark);
}

.book-prep-title {
  margin: 6px 0 8px;
  font-size: 20px;
  color: var(--bk-navy);
}

.book-prep-lede {
  margin: 0 auto 16px;
  max-width: 46ch;
  font-size: 15px;
  line-height: 1.6;
}

.book-prep-btn {
  display: inline-block;
  text-decoration: none;
}

/* The out is quiet but never hidden — a client who feels cornered into a
   questionnaire has had a worse experience than the flow this replaces. */
.book-prep-later {
  display: block;
  margin: 12px auto 0;
  font-size: 14px;
  opacity: 0.75;
}

.book-prep-deferred {
  margin: 18px 0 0;
  text-align: center;
  font-size: 14px;
  opacity: 0.8;
}

/* The manage link stands alone, quietly — not another bordered card
   nested inside the confirmation flow (audit D9). */
.book-manage-quiet {
  margin-top: 20px;
  text-align: center;
}

.book-manage-quiet p {
  margin: 0 0 8px;
  font-size: 14px;
  opacity: 0.75;
}

/* ── Confirmation ──
   The "You're booked" mark, the day/time, and the how-it-starts line
   all live in the hero band now (title / kicker / lede). */

/* Add-to-calendar row on the confirmation screen (audit M2). */
.book-calendar-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin: 14px 0;
}

.book-calendar-row .book-pill {
  text-decoration: none;
  display: inline-block;
}

/* ── Steps ── */

.book-step {
  /* Opacity only (2026-08-25 audit C2) — a translateY here shifts the
     grid's own buttons out from under a click that lands mid-animation,
     the one mechanism that could plausibly explain a dead first click
     right after a step transition. Opacity never moves hit targets. */
  animation: book-fade 160ms ease;
}

@keyframes book-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.book-back {
  margin: 8px 0 0;
}

/* Code input */
.book-code-input {
  letter-spacing: 0.5em;
  font-size: 22px !important;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 640px) {
  .book-hero {
    padding-bottom: 76px;
  }
  .book-header {
    padding: 18px 0;
  }
  .book-wordmark {
    font-size: 15px;
  }
  .book-hero-inner {
    gap: 16px;
    padding-top: 28px;
  }
  .book-hero-lede {
    font-size: 16px;
  }
  .book-hero-title {
    letter-spacing: 0.02em;
  }
  .book-step-item {
    font-size: 10.5px;
    letter-spacing: 0.08em;
  }
  .book-step-item + .book-step-item::before {
    width: 10px;
    margin: 0 8px;
  }
  .book-body {
    margin-top: -52px;
  }
  .book-main {
    padding: 18px;
  }
}

@media (max-width: 480px) {
  .book-times {
    grid-template-columns: repeat(3, 1fr);
  }
  .book-daystrip {
    gap: 4px;
  }
  .book-daychip {
    padding: 7px 1px;
  }
  .book-daychip-meta {
    font-size: 9.5px;
  }
  .book-card {
    padding: 16px;
  }
}

/* Turnstile (anonymous guests only — 2026-08-15 audit fix). The managed
   widget sizes itself; this just keeps its slot from jamming the form. */
.book-public .book-turnstile {
  margin: 4px 0 2px;
  min-height: 65px;
}

/* Three people across (2026-09-04): the cards are too narrow for the
   opening + button to share a row, so the foot stacks. */
.book-advisor-choices--three .book-advisor-choice-foot {
  flex-direction: column;
  align-items: stretch;
}

.book-advisor-choices--three .book-advisor-choice .book-btn {
  width: 100%;
}

/* A link inside the hero lede (the error boundary's phone number) reads
   white, not blue-on-navy (2.8:1 fails for body-weight text). */
.book-hero-lede a {
  color: var(--bk-white);
  font-weight: 700;
}

/* The swoosh stroke rides currentColor so the yellow stays a token. */
.book-swoosh svg {
  color: var(--bk-yellow);
}

/* ── The connected experience (2026-09-05) ─────────────────────────
   A held time on the navy band, and the questions inline under it. */
.book-held-chip {
  display: inline-block;
  margin-left: 10px;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: "Open Sans", sans-serif;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.02em;
  color: var(--bk-yellow);
  background: rgba(254, 215, 102, 0.16);
  border: 1px solid rgba(254, 215, 102, 0.45);
  vertical-align: middle;
  font-variant-numeric: tabular-nums;
}
.book-hold-hint {
  text-align: center;
  margin: 8px 0 0;
}
.book-questions .fpub-section:first-of-type h2 {
  margin-top: 0;
}
.book-questions .fpub-nav {
  margin-top: 18px;
}
.book-answers-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--bk-border, #e0e6ec);
  font-size: 14px;
}
.book-answers-row strong {
  color: #1f8a4c;
}
